import React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/Card'
import { formatToLongDate, formatToLongDateWithTimeZone } from 'utils/date-util'
import { formatMoney } from 'utils/money-util'
import JobViewModel from '../JobViewModel'
import styles from './JobSummaryCard.sass'
import { pluralize } from 'utils/pluralization-util'
import { capitalize } from 'lodash'
import { useHistory } from 'react-router'

interface JobSummaryItemProps {
  label: string
  value: string
  buttonText?: string
  onClick?: () => void
  dataTest?: string
  buttonDataTest?: string
}

const JobSummaryItem = ({
  label,
  value,
  buttonText,
  onClick,
  dataTest,
  buttonDataTest,
}: JobSummaryItemProps) => {
  return (
    <div className={styles.jobSummaryItem}>
      <div className={styles.jobSummaryItemLabel}>{label}</div>
      <div className={styles.jobSummaryItemValueContainer}>
        <div className={styles.jobSummaryItemValue} data-test={dataTest}>
          {value}
        </div>
        {buttonText && onClick && (
          <div className={styles.jobSummaryItemAction}>
            <Button
              className={styles.jobSummaryItemButton}
              type="text"
              onClick={onClick}
              dataTest={buttonDataTest}
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

interface JobSummaryCardProps {
  vm: JobViewModel
}

export default observer(function JobSummaryCard({ vm }: JobSummaryCardProps) {
  const history = useHistory()
  const consultation = vm.job.consultation
  const bookedStartTime = consultation?.bookedTime?.startTime
  const consultationCallTime = consultation?.skipScheduling
    ? 'Message your Client to schedule a consultation call'
    : vm.consultationIsPendingScheduling
      ? vm.consultationPendingSchedulingText
      : bookedStartTime
        ? formatToLongDateWithTimeZone(bookedStartTime)
        : undefined

  return (
    <Card>
      <CardHeader>
        <CardTitle>Job summary</CardTitle>
      </CardHeader>
      <CardContent scroll>
        <JobSummaryItem label="ID" value={vm.job.shortId} />
        <JobSummaryItem label="Workspace" value={vm.jobWorkspace.name} />

        {vm.job.hasActiveConsultation && consultationCallTime && (
          <JobSummaryItem
            label="Consultation call time"
            value={consultationCallTime}
            dataTest="job-consultation-time"
          />
        )}
        {vm.job.canViewLegendNameAsAdmin && (
          <JobSummaryItem
            label="Legend"
            value={vm.legendText}
            dataTest="job-legend-name"
          />
        )}
        {vm.serviceItemsListLength > 0 && (
          <JobSummaryItem
            label="Service items"
            value={`${vm.serviceItemsListLength} ${pluralize(
              'item',
              vm.serviceItemsListLength,
            )}`}
            buttonText="View list"
            onClick={() => vm.serviceItemListVM.toggleShowing()}
          />
        )}
        {vm.job.jobBundle && (
          <JobSummaryItem label="Bundle" value={vm.job.jobBundle.name} />
        )}
        {vm.job.shouldShowProviderCutAmount && (
          <JobSummaryItem
            label="Pro cut"
            value={vm.job.formattedProviderCutAmount}
            dataTest="job-pro-cut-amount"
          />
        )}
        {vm.job.dateSubmitted && (
          <JobSummaryItem
            label="Date submitted"
            value={formatToLongDate(vm.job.dateSubmitted)}
          />
        )}
        {vm.job.dateDeadline && (
          <JobSummaryItem
            label="Deadline"
            value={formatToLongDate(vm.job.dateDeadline)}
          />
        )}

        {vm.job.containsPrepAndReviewLink && (
          <JobSummaryItem
            label="Prep and review"
            value={`${capitalize(vm.job.prepAndReviewJobText)} job`}
            buttonText={`View ${vm.job.prepAndReviewJobLinkType?.toLowerCase()} job`}
            onClick={() =>
              history.push(`/jobs/${vm.job.prepAndReviewJobLinkId}`)
            }
            dataTest={`prep-and-review-job-type-${vm.job.prepAndReviewJobText}`}
            buttonDataTest={`prep-and-review-job-type-${vm.job.prepAndReviewJobLinkType}-btn`}
          />
        )}

        {vm.job.shouldShowJobTotal && (
          <JobSummaryItem
            label="Total"
            value={formatMoney(vm.job.total)}
            dataTest="job-total"
          />
        )}
      </CardContent>
    </Card>
  )
})
